import { useGlobalModalContext } from 'context/GlobalModal';
import { DailyPuzzle } from 'types/components/DailyPuzzle';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { rawGetPuzzlesById } from 'services';
import {
  createDailyPuzzle,
  getDailyPuzzleById,
  getDailyPuzzleByPuzzleId,
  updateDailyPuzzleById,
} from 'services/api/dailyPuzzlesApi';
import { isAbleToMutateDailyPuzzle } from 'modules/dashboard/pages/DailyPuzzlesScreen/utils/helpers';

import { getDailyPuzzleDocumentId } from '../utils/helpers';

export type CreateEditDailyPuzzleForm = {
  puzzleId: string;
  date: Date;
};

type useAddEditDailyPuzzleProps = {
  editDailyPuzzleId?: string;
  onSaveCallback?: () => void;
};

const useAddEditDailyPuzzle = ({
  editDailyPuzzleId,
  onSaveCallback,
}: useAddEditDailyPuzzleProps) => {
  const [puzzleData, setPuzzleData] = useState<any>();
  const [isLoading, setLoading] = useState<boolean>(false);
  const { hideModal } = useGlobalModalContext();

  const { handleSubmit, control, setValue, watch } = useForm<CreateEditDailyPuzzleForm>();

  const { date, puzzleId } = watch();

  const submit = useCallback(
    handleSubmit(async (data: CreateEditDailyPuzzleForm) => {
      if (isLoading || (editDailyPuzzleId && !isAbleToMutateDailyPuzzle(data.date))) return;
      setLoading(true);
      try {
        let puzzleData;
        if (puzzleId) {
          const puzzleDataDocSnap = await rawGetPuzzlesById(puzzleId);
          if (puzzleDataDocSnap.exists()) puzzleData = puzzleDataDocSnap.data();
        }
        if (!puzzleData) {
          setLoading(false);
          return toast.error('Puzzle ID not valid.');
        }
        if (puzzleData && puzzleData.status !== 'approved') {
          setLoading(false);
          return toast.error('Puzzle not approved.');
        }
        if (puzzleData.premiumTier) {
          setLoading(false);
          return toast.error("Premium Puzzle can't be set as Daily Puzzle.");
        }
        if (!date) {
          setLoading(false);
          return toast.error('Date not valid.');
        }
        if (editDailyPuzzleId) {
          const doesPuzzleInDailyPuzzleExist = await getDailyPuzzleByPuzzleId(puzzleId);
          const fetchedPuzzleId = doesPuzzleInDailyPuzzleExist?.[0]?.id;
          if (fetchedPuzzleId && fetchedPuzzleId !== editDailyPuzzleId) {
            setLoading(false);
            return toast.error('Puzzle already exist.');
          }
          await updateDailyPuzzleById(getDailyPuzzleDocumentId(data.date), {
            date: data.date,
            puzzleId: data.puzzleId,
            iconUrl: puzzleData?.iconURL,
            puzzleName: puzzleData?.name,
          });
          toast.success('Daily Puzzle Updated.');
        } else {
          const dayExist = await getDailyPuzzleById(getDailyPuzzleDocumentId(data.date));
          const doesPuzzleInDailyPuzzleExist = await getDailyPuzzleByPuzzleId(puzzleId);
          if (dayExist) {
            setLoading(false);
            return toast.error('Day already exist.');
          }
          if (doesPuzzleInDailyPuzzleExist?.length) {
            setLoading(false);
            return toast.error('Puzzle already exist.');
          }
          await createDailyPuzzle(getDailyPuzzleDocumentId(data.date), {
            date: data.date,
            puzzleId: data.puzzleId,
            iconUrl: puzzleData?.iconURL,
            puzzleName: puzzleData?.name,
          });
          toast.success('Daily Puzzle Created.');
        }
        if (onSaveCallback) onSaveCallback();
        hideModal();
      } catch (err) {
        console.error(err);
        toast.error('Failed to create Daily Puzzle.');
      }
      setLoading(false);
    }),
    [isLoading, setLoading, date, puzzleData, puzzleId, editDailyPuzzleId, hideModal],
  );

  useEffect(() => {
    (async () => {
      if (puzzleId) {
        setLoading(true);
        const docSnap = await rawGetPuzzlesById(puzzleId);
        if (docSnap.exists()) setPuzzleData({ ...docSnap.data(), uuid: docSnap.id });
        else setPuzzleData(null);
      } else {
        setPuzzleData(null);
      }
      setLoading(false);
    })();
  }, [puzzleId]);

  useEffect(() => {
    (async () => {
      if (editDailyPuzzleId) {
        const dailyPuzzle: DailyPuzzle | null = await getDailyPuzzleById(editDailyPuzzleId);
        if (dailyPuzzle?.puzzleId) setValue('puzzleId', dailyPuzzle.puzzleId);
        if (dailyPuzzle?.date) setValue('date', dailyPuzzle.date);
      }
    })();
  }, []);

  return {
    isLoading: isLoading,
    submit,
    control,
    puzzleId,
    date,
    setValue,
    puzzleData,
  };
};

export default useAddEditDailyPuzzle;
